import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const CategoryPageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1e1d1d 0%, #2c2c2c 100%);
  color: #ffffff;
  padding: 8rem 2rem 6rem 2rem;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const BackButton = styled.button`
  position: fixed;
  top: 2rem;
  left: 2rem;
  padding: 1rem 2.5rem;
  background: rgba(255, 114, 0, 0.25);
  border: 2px solid rgba(255, 114, 0, 0.6);
  border-radius: 2rem;
  color: #ff7200;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  z-index: 1000;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);

  &:hover {
    background: rgba(255, 114, 0, 0.35);
    transform: translateX(-5px);
    box-shadow: 0 6px 20px rgba(255, 114, 0, 0.5);
  }

  @media (max-width: 768px) {
    top: 1rem;
    left: 1rem;
    padding: 0.8rem 1.5rem;
    font-size: 1rem;
    
    ${props => props.isScrolled && `
      background: rgba(255, 114, 0, 0.35);
      border-color: rgba(255, 114, 0, 0.8);
    `}

    &:hover {
      transform: ${props => props.isScrolled ? 'translateY(-3px)' : 'translateX(-5px)'};
    }
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;

  h1 {
    font-size: 3rem;
    margin-bottom: 1rem;
    text-transform: uppercase;
    letter-spacing: 2px;
    color: #ff7200;
    background: none;
    -webkit-background-clip: unset;
    -webkit-text-fill-color: #ff7200;
    background-clip: unset;
  }

  p {
    font-size: 1.2rem;
    color: #ffffff;
  }
`;

const ClientsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const ClientCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 2px solid rgba(255, 114, 0, 0.3);
  border-radius: 1.5rem;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  opacity: ${props => props.visible ? 1 : 0};
  transform: translateY(${props => props.visible ? '0' : '20px'});
  transition-delay: ${props => props.delay}ms;

  &:hover {
    background: rgba(255, 114, 0, 0.25);
    border-color: rgba(255, 114, 0, 0.8);
    transform: translateY(-10px);
    box-shadow: 0 15px 40px rgba(255, 114, 0, 0.3);
  }

  h3 {
    font-size: 1.8rem;
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #ffffff;
  }

  p {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.9rem;
  }
`;

const CategoryPage = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    setVisible(true);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const categoryData = {
    'gestao-midias': {
      title: 'Gestão de Mídias',
      description: 'Gerenciamento estratégico de redes sociais e presença digital',
      clients: [
        { name: 'Bee Happy' },
        { name: 'Center Marc' },
        { name: 'L3 Engenharia Ambiental' },
        { name: 'Lirios' },
        { name: 'Movimento Eco' },
        { name: 'Pet Life' },
        { name: 'Red Nose' },
        { name: 'Sanclá' },
        { name: 'Pericles' },
        { name: 'Arte Esportiva' }
      ]
    },
    'comunicacao-integrada': {
      title: 'Comunicação Integrada',
      description: 'Estratégias 360° de comunicação corporativa',
      clients: [
        { name: 'Bee Happy' },
        { name: 'Red Nose' }
      ]
    },
    videos: {
      title: 'Vídeos',
      description: 'Produção audiovisual e motion graphics',
      clients: [
        { name: 'Bee Happy' },
        { name: 'L3 Engenharia Ambiental' },
        { name: 'Movimento Eco' },
        { name: 'Pericles' }
      ]
    }
  };

  const currentCategory = categoryData[category];

  if (!currentCategory) {
    return <div>Categoria não encontrada</div>;
  }

  // Mapeamento de nomes de clientes para slugs
  const clientSlugMap = {
    'Bee Happy': 'bee-happy',
    'Center Marc': 'center-marc',
    'L3 Engenharia Ambiental': 'l3-engenharia-ambiental',
    'Lirios': 'lirios',
    'Movimento Eco': 'movimento-eco',
    'Pet Life': 'pet-life',
    'Red Nose': 'red-nose',
    'Sanclá': 'sancla',
    'Pericles': 'pericles',
    'Arte Esportiva': 'arte-esportiva'
  };

  const handleClientClick = (clientName) => {
    const slug = clientSlugMap[clientName] || clientName.toLowerCase().replace(/[^a-z0-9]/g, '-');
    navigate(`/portfolio/${category}/${slug}`);
  };

  const handleBackClick = () => {
    const isMobile = window.innerWidth <= 768;
    
    // No mobile, verifica se está rolado
    if (isMobile && window.scrollY > 100) {
      // Se estiver rolada no mobile, volta para o topo
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Se estiver no topo (mobile) ou em desktop, navega para a página anterior
      navigate('/');
      setTimeout(() => {
        const portfolioSection = document.getElementById('portfolio');
        if (portfolioSection) {
          portfolioSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  return (
    <CategoryPageWrapper>
      <Container>
        <BackButton onClick={handleBackClick} isScrolled={isScrolled}>
          {isScrolled ? '↑ Voltar ao Topo' : '← Voltar para Portfólio'}
        </BackButton>
        <PageHeader>
          <h1>{currentCategory.title}</h1>
          <p>{currentCategory.description}</p>
        </PageHeader>
        <ClientsGrid>
          {currentCategory.clients.map((client, index) => (
            <ClientCard
              key={index}
              visible={visible}
              delay={index * 100}
              onClick={() => handleClientClick(client.name)}
            >
              <h3>{client.name}</h3>
            </ClientCard>
          ))}
        </ClientsGrid>
      </Container>
    </CategoryPageWrapper>
  );
};

export default CategoryPage;
